import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useTransition,
} from "react";
import { Chip } from "../../chip";

const accessNested = (obj, key) => {
  return key
    .split(".")
    .reduce(
      (_obj, _key) => (_obj && typeof _obj === "object" ? _obj[_key] : null),
      obj
    );
};

export function ChipList({
  placeholder,
  name,
  label,
  className,
  formik,
  options,
  readOnly,
  optionId = "key",
  optionName = "label",
  disabled = false
}) {
  const formikValue = accessNested(formik.values, name);

  const [isOpen, setIsOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  const [filteredOptions, setFilteredOptions] = useState([]);
  const [selected, setSelected] = useState([]);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const dropdownRef = useRef();
  const inputRef = useRef();

  const [positionBelow, setPositionBelow] = useState(true);
  // Contains the last changed field either formik or chip list selection
  const changeSourceRef = useRef(null);
  const [isPending, startTransition] = useTransition();

  useLayoutEffect(() => {
    const calculatePosition = () => {
      const rect = inputRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      setPositionBelow(rect.bottom + 260 <= windowHeight);
    };

    const handleScroll = () => {
      calculatePosition();
    };

    calculatePosition();

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isOpen]);


  useEffect(() => {
    const current_selected_value = selected?.length ? selected.map((_i) => _i[optionId]) : [];
    if (JSON.stringify(current_selected_value) !== JSON.stringify(formikValue)) {
      changeSourceRef.current = 'CHIP_LIST_SELECTION';
    }
  }, [selected]);
  
  useEffect(() => {
    const current_formik_value = formikValue ? options.filter((_i) => formikValue.includes(_i[optionId])) : [];
    if (formikValue && JSON.stringify(current_formik_value) !== JSON.stringify(selected)) {
      changeSourceRef.current = 'FORMIK';
    }
  }, [formikValue]);

  
  // This useEffect can only be declared after the `changeSourceRef` is changed.
  useEffect(() => {
    if (changeSourceRef.current === 'FORMIK') {
      // Update selected when formikValue changes
      setSelected(options.filter((_i) => formikValue.includes(_i[optionId])));
      changeSourceRef.current = null; // Reset after sync
    } else if (changeSourceRef.current === 'CHIP_LIST_SELECTION') {
      // Update formik when selected changes
      formik.setFieldValue(
        name,
        selected.map((_i) => _i[optionId])
      );
      changeSourceRef.current = null; // Reset after sync
    }
  }, [formikValue, selected]);

  useEffect(() => {
    setFilteredOptions(
      options?.filter(
        (option) =>
          (!keyword
            ? true
            : option[optionName]
                ?.toString()
                .toLowerCase()
                .includes(keyword.toLowerCase()) ||
              option[optionId]
                .toString()
                .toLowerCase()
                .includes(keyword.toLowerCase())) &&
          !selected.map((_item) => _item[optionId]).includes(option[optionId])
      )
    );
  }, [keyword, selected]);

  useEffect(() => {
    if (
      isOpen &&
      focusedIndex >= 0 &&
      filteredOptions.length > 0 &&
      filteredOptions.length > focusedIndex
    ) {
      dropdownRef.current.children[focusedIndex].scrollIntoView({
        block: "nearest",
      });
    }
  }, [focusedIndex, isOpen, filteredOptions]);


  const handleInput = (input) => {
    let value = input.target.value;
    setKeyword(value);
  };

  const handleOptionClick = (option) => {
    setSelected([...selected, option]);
  };

  const handleOptionEnter = () => {
    if (filteredOptions?.length > 0) {
      setSelected([...selected, filteredOptions[0]]);
    }
  };

  const handleBackspace = () => {
    if (!keyword && selected.length > 0) {
      setSelected(selected?.slice(0, selected.length - 1));
    }
  };

  const handleRemove = (item) => {
    setSelected(selected.filter((_selected) => _selected !== item));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (focusedIndex >= 0) {
        handleOptionClick(filteredOptions[focusedIndex]);
        setFocusedIndex(0);
      } else {
        handleOptionEnter();
      }
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocusedIndex((prevIndex) =>
        prevIndex + 1 >= filteredOptions.length
          ? filteredOptions.length - 1
          : prevIndex + 1
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusedIndex((prevIndex) => (prevIndex - 1 < 0 ? 0 : prevIndex - 1));
    } else if (e.key === "Backspace") {
      handleBackspace();
    } else if (e.key === "Escape") {
      inputRef.current.blur();
    }
  };

  return (
    <>
      <div className={`${className || ""} relative`}>
        <label
          htmlFor="message"
          class="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
        >
          {label}
        </label>
        <div
          id="message"
          onClick={() => {
            if (!readOnly && !disabled) inputRef.current?.focus();
          }}
          class={`${
            readOnly && "!border-0"
          } ${
            disabled && "opacity-50 cursor-not-allowed bg-gray-100"
          } min-h-20 flex flex-row block p-2.5 w-full text-sm text-gray-900 bg-transparent rounded-lg border border-gray-300 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500`}
          placeholder={placeholder}
        >
          <div className="flex flex-wrap">
            {selected?.map((item) => (
              <Chip
                label={item[optionName]}
                className={isOpen && "z-50"}
                dismissible={!readOnly && !disabled}
                onDismiss={() => {
                  if (!readOnly) {
                    handleRemove(item);
                    setIsOpen(false);
                  }
                }}
              />
            ))}

            <div className="relative">
              <input
                ref={inputRef}
                disabled={disabled}
                onBlur={() => {
                  setIsOpen(false);
                }}
                onFocus={() => {
                  if (!disabled) setIsOpen(true);
                }}
                onKeyDown={(e) => {
                  handleKeyDown(e);
                }}
                value={keyword}
                className={`${
                  readOnly && "hidden"
                } ${
                  disabled && "cursor-not-allowed"
                } focus:ring-0 ring-0 outline-none p-2.5 bg-transparent`}
                onChange={handleInput}
              />
              {isOpen && filteredOptions?.length > 0 && (
                <>
                  <div
                    className="fixed inset-0 opacity-25 z-40 p-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      setKeyword("");
                      setIsOpen(false);
                    }} // Clicking on the backdrop closes the dropdown
                  ></div>
                  <ul
                    ref={dropdownRef}
                    className={`${
                      positionBelow ? "" : "bottom-10"
                    } [&:hover_li]:bg-transparent absolute mt-2 bg-white border border-gray-200 w-auto z-50 p-3 max-h-[250px] overflow-y-auto 
            rounded-xl bg-white leading-6 shadow-lg ring-1 ring-gray-900/5`}
                    role="listbox"
                    tabIndex="-1"
                    aria-labelledby="dropdown-label"
                  >
                    {filteredOptions.map((option, index) => (
                      <li
                        key={option[optionId]}
                        className={`
                        ${index === focusedIndex ? "!bg-indigo-300/50" : ""}
                        cursor-pointer px-2 py-1 focus:!bg-indigo-300/50 whitespace-nowrap`}
                        onMouseEnter={(e) => {
                          startTransition(() => setFocusedIndex(index));
                        }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                        }}
                        onClick={() => {
                          handleOptionClick(option);
                        }}
                      >
                        {option[optionName]}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
